import { announce } from './announce.js';

let detailsId = 0;

/**
 * Table behaviors, delegated from document so they survive Livewire morphs.
 */
export function registerTableBehaviors(t) {
    // Expandable rows and collapsible groups.
    document.addEventListener('click', (event) => {
        const expand = event.target.closest('[data-mellow-table-expand]');
        if (expand) {
            const details = expand.closest('tr')?.nextElementSibling;
            if (!details?.classList.contains('mellow-table-details')) return;
            const open = expand.getAttribute('aria-expanded') !== 'true';
            details.id ||= `mellow-table-details-${++detailsId}`;
            expand.setAttribute('aria-controls', details.id);
            expand.setAttribute('aria-expanded', String(open));
            details.hidden = !open;
            return;
        }

        const toggle = event.target.closest('[data-mellow-table-group-toggle]');
        if (toggle) {
            const open = toggle.getAttribute('aria-expanded') !== 'true';
            toggle.setAttribute('aria-expanded', String(open));
            toggle.closest('.mellow-table-group').toggleAttribute('data-collapsed', !open);
        }
    });

    // Keyboard reordering for wire:sort: ↑/↓ on the handle move the row and call the same Livewire method.
    document.addEventListener('keydown', (event) => {
        const handle = event.target.closest?.('[data-mellow-table-handle]');
        if (!handle || !['ArrowUp', 'ArrowDown'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;

        const row = handle.closest('tr');
        const body = row?.parentElement;
        const method = body?.getAttribute('wire:sort')?.match(/^\s*([\w$]+)/)?.[1];
        if (!method) return;
        event.preventDefault();

        const items = [...body.children].filter((child) => child.hasAttribute('wire:sort:item'));
        const from = items.indexOf(row);
        const to = from + (event.key === 'ArrowUp' ? -1 : 1);
        if (from === -1 || to < 0 || to >= items.length) return;

        // Move the neighbour, not the focused row: moving a focused element would lose focus.
        const neighbour = items[to];
        if (to < from) row.after(neighbour);
        else row.before(neighbour);

        const raw = row.getAttribute('wire:sort:item');
        const key = /^-?\d+$/.test(raw) ? Number(raw) : raw.replace(/^['"]|['"]$/g, '');
        // Livewire may replace the row while morphing: keep the focus on its handle for the next key press.
        const rowKey = row.getAttribute('wire:key');
        const refocus = () => {
            if (document.activeElement?.closest?.('[data-mellow-table-handle]')) return;
            const current = rowKey ? body.querySelector(`:scope > tr[wire\\:key="${CSS.escape(rowKey)}"]`) : row;
            current?.querySelector('[data-mellow-table-handle]')?.focus();
        };
        Promise.resolve(wireOf(body)?.[method](key, to)).finally(() => requestAnimationFrame(refocus));
        announce(t('table.moved', { position: to + 1, total: items.length }));
    });

    // pagination:scroll-to – bring the table (or a custom target) back into view when paging.
    document.addEventListener('click', (event) => {
        const nav = event.target.closest('.mellow-pagination-link')?.closest('[data-mellow-scroll-to]');
        if (!nav) return;

        const selector = nav.dataset.mellowScrollTo;
        const target = (selector && document.querySelector(selector)) || nav.closest('.mellow-table-container') || nav;
        if (target.getBoundingClientRect().top >= 0) return;

        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    });

    // Row selection: "select all" on the page, header state and the bulk actions bar.
    document.addEventListener('change', (event) => {
        const box = event.target;
        if (!(box instanceof HTMLInputElement)) return;
        const container = box.closest('.mellow-table-container');
        if (!container) return;

        if (box.closest('[data-mellow-table-columns-toggle]')) {
            toggleColumn(container, box.closest('[data-column]').dataset.column, box.checked);
            return;
        }

        if (box.hasAttribute('data-mellow-table-select-all')) selectAll(container, box.checked);
        if (box.matches('[data-mellow-table-select], [data-mellow-table-select-all]')) sync(container, t);
    });

    const syncAll = () => document.querySelectorAll('.mellow-table-container').forEach((container) => sync(container, t));
    const hook = () => window.Livewire.hook('commit', ({ succeed }) => succeed(() => requestAnimationFrame(syncAll)));
    if (window.Livewire) hook();
    else document.addEventListener('livewire:init', hook);
    document.addEventListener('livewire:initialized', () => requestAnimationFrame(syncAll));
    document.addEventListener('livewire:navigated', syncAll);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncAll);
    else syncAll();
}

/** Row checkboxes of this table (not of nested tables). */
function rows(container) {
    return [...container.querySelectorAll('[data-mellow-table-select]')].filter((box) => box.closest('.mellow-table-container') === container);
}

/** { name, live } of the wire:model on a row checkbox. */
function wireModel(box) {
    const attribute = [...(box?.attributes ?? [])].find((attr) => attr.name === 'wire:model' || attr.name.startsWith('wire:model.'));
    return attribute ? { name: attribute.value, live: attribute.name.includes('.live') } : null;
}

/** The $wire of the Livewire component around an element (Livewire.find returns it directly). */
function wireOf(el) {
    const id = el.closest('[wire\\:id]')?.getAttribute('wire:id');
    return id && window.Livewire ? window.Livewire.find(id) : null;
}

function selectAll(container, checked) {
    const boxes = rows(container).filter((box) => !box.disabled);
    boxes.forEach((box) => (box.checked = checked));

    // One Livewire update for the whole page instead of one request per row; keeps selections of other pages.
    const model = wireModel(boxes[0]);
    const wire = model && wireOf(container);
    if (wire) {
        const values = boxes.map((box) => box.value);
        const current = (wire.$get(model.name) ?? []).map(String);
        const next = checked ? [...new Set([...current, ...values])] : current.filter((value) => !values.includes(value));
        wire.$set(model.name, next, model.live);
    }
}

/* ── Column visibility ─────────────────────────────────────────── */

/** Per page and table: its id or caption, else its position (Livewire ids change on every load). */
function storageKey(container) {
    const key = container.dataset.mellowTableKey ?? [...document.querySelectorAll('.mellow-table-container')].indexOf(container);
    return `mellow:table-columns:${location.pathname}#${key}`;
}

function hiddenColumns(container) {
    try {
        return JSON.parse(localStorage.getItem(storageKey(container)) || '[]');
    } catch {
        return [];
    }
}

function toggleColumn(container, label, visible) {
    const hidden = new Set(hiddenColumns(container));
    if (visible) hidden.delete(label);
    else hidden.add(label);
    try {
        localStorage.setItem(storageKey(container), JSON.stringify([...hidden]));
    } catch {
        // Storage unavailable (private mode): the choice lasts until the next page load.
        container._mellowHidden = [...hidden];
    }
    columns(container);
}

/** Builds the column toggle menu from the headers and hides the chosen columns. */
function columns(container) {
    const toggle = container.querySelector('[data-mellow-table-columns-toggle]');
    const table = container.querySelector('.mellow-table');
    if (!toggle || !table) return;

    const headers = [...(table.tHead?.rows[0]?.cells ?? [])].map((th, index) => ({
        index,
        label: (th.dataset.label || th.textContent).trim(),
        hideable: !th.hasAttribute('data-mellow-not-hideable') && !th.classList.contains('mellow-table-select-column'),
    })).filter((column) => column.hideable && column.label);

    const stored = container._mellowHidden ?? hiddenColumns(container);
    const hidden = new Set(stored.filter((label) => headers.some((column) => column.label === label)));

    // Rebuild the menu only when the columns changed (DOM writes would trigger observers again);
    // otherwise just update the check states – rebuilding an open menu would lose the focused item.
    const menu = toggle.querySelector('[role="menu"]');
    const signature = JSON.stringify(headers.map((column) => column.label));
    menu?.querySelectorAll('[data-column]').forEach((item) => {
        const visible = !hidden.has(item.dataset.column);
        if (item.getAttribute('aria-checked') !== String(visible)) item.setAttribute('aria-checked', String(visible));
        const input = item.querySelector('input');
        if (input.checked !== visible) input.checked = visible;
    });
    if (menu && menu.dataset.signature !== signature) {
        menu.dataset.signature = signature;
        const icon = menu.querySelector('template[data-mellow-menu-check]');
        menu.querySelectorAll('[data-column]').forEach((item) => item.remove());
        headers.forEach((column) => {
            const item = document.createElement('div');
            item.className = 'mellow-menu-item';
            item.setAttribute('role', 'menuitemcheckbox');
            item.setAttribute('aria-checked', String(!hidden.has(column.label)));
            item.tabIndex = -1;
            item.dataset.choice = '';
            item.dataset.keepOpen = '';
            item.dataset.column = column.label;

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.hidden = true;
            input.tabIndex = -1;
            input.checked = !hidden.has(column.label);

            const label = document.createElement('span');
            label.className = 'mellow-menu-item-label';
            label.textContent = column.label;

            item.append(input, ...(icon ? [icon.content.cloneNode(true)] : []), label);
            menu.append(item);
        });
    }

    // One stylesheet per table in <head>, outside of anything Livewire morphs.
    let style = document.head.querySelector(`style[data-mellow-table="${CSS.escape(container.id)}"]`);
    const selector = `#${CSS.escape(container.id)} > .mellow-table-wrapper > .mellow-table > * > tr`;
    const css = headers.filter((column) => hidden.has(column.label))
        .map((column) => `${selector} > :nth-child(${column.index + 1}):not([colspan]) { display: none; }`)
        .join('\n');
    if (!style && css) {
        style = document.createElement('style');
        style.dataset.mellowTable = container.id;
        if (window.Mellow?.nonce) style.nonce = window.Mellow.nonce;
        document.head.append(style);
    }
    if (style && style.textContent !== css) style.textContent = css;
}

function sync(container, t) {
    columns(container);

    const boxes = rows(container);
    const all = container.querySelector('[data-mellow-table-select-all]');
    const checked = boxes.filter((box) => box.checked).length;

    if (all) {
        all.checked = checked > 0 && checked === boxes.length;
        all.indeterminate = checked > 0 && checked < boxes.length;
    }

    const bar = container.querySelector('[data-mellow-table-bulk]');
    if (!bar) return;

    // Bound to Livewire, the selection may include rows of other pages.
    const wire = bar.dataset.model && wireOf(container);
    const selected = wire ? (wire.$get(bar.dataset.model) ?? []).length : checked;
    bar.hidden = selected === 0;
    const text = bar.dataset.countLabel.replace(':count', selected);
    const count = bar.querySelector('.mellow-table-bulk-count');
    if (count.textContent !== text) count.textContent = text;
}
